import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, CheckCircle } from "lucide-react";

const getTicketPriorityColor = (priority) => {
  switch (priority) {
    case "urgent": return "bg-red-600 text-white";
    case "high": return "bg-orange-500 text-white";
    case "medium": return "bg-yellow-500 text-black";
    case "low": return "bg-gray-500 text-white";
    default: return "bg-gray-500 text-white";
  }
};

const getTicketStatusColor = (status) => {
  switch (status) {
    case "open": return "bg-blue-600 text-white";
    case "in_progress": return "bg-purple-600 text-white";
    case "waiting_response": return "bg-amber-500 text-black";
    case "resolved": return "bg-green-600 text-white";
    case "closed": return "bg-gray-600 text-white";
    default: return "bg-gray-500 text-white";
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });
};

export default function TicketsTab({
  tickets,
  ticketStats,
  onViewTicket,
  onResolveTicket
}) {
  return (
    <div className="space-y-4" data-testid="tickets-tab-content">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Support Tickets</h2>
        <div className="flex gap-2">
          {ticketStats && (
            <div className="flex gap-2 text-sm">
              <Badge className="bg-blue-500">Open: {ticketStats.open}</Badge>
              <Badge className="bg-purple-500">In Progress: {ticketStats.in_progress}</Badge>
              <Badge className="bg-amber-500 text-white">Waiting: {ticketStats.waiting_response}</Badge>
            </div>
          )}
        </div>
      </div>
      
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left p-4 text-gray-600 font-medium">Ticket</th>
                <th className="text-left p-4 text-gray-600 font-medium">Parent</th>
                <th className="text-left p-4 text-gray-600 font-medium">Category</th>
                <th className="text-left p-4 text-gray-600 font-medium">Priority</th>
                <th className="text-left p-4 text-gray-600 font-medium">Status</th>
                <th className="text-left p-4 text-gray-600 font-medium">Created</th>
                <th className="text-left p-4 text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <p className="font-medium text-gray-900">{ticket.ticket_number}</p>
                    <p className="text-sm text-gray-500 truncate max-w-xs">{ticket.subject}</p>
                  </td>
                  <td className="p-4 text-sm text-gray-700">{ticket.parent_name}</td>
                  <td className="p-4">
                    <Badge variant="outline" className="border-gray-300 text-gray-700">
                      {ticket.category}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={getTicketPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={getTicketStatusColor(ticket.status)}>
                      {ticket.status?.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm text-gray-500">{formatDate(ticket.created_at)}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewTicket(ticket)}
                        className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        data-testid={`view-ticket-${ticket.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {ticket.status !== "resolved" && ticket.status !== "closed" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onResolveTicket(ticket.id)}
                          className="text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                          title="Mark Resolved"
                          data-testid={`resolve-ticket-${ticket.id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">
                    No support tickets yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
